import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Sparkles, FolderOpen, Users, CreditCard, Building2, Bell, LogOut, GraduationCap, KeyRound, Siren, ClipboardCheck, FolderKanban, Menu, MessageCircle, Coins, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth, useTheme } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo, Loader, ThemeToggle } from "@/components/brand";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { AskSaflr } from "@/components/app/AskSaflr";

const LINKS = [
  ["Command Centre", "/app", LayoutDashboard, true],
  ["Create with AI", "/app/generate", Sparkles],
  ["Documents", "/app/documents", FolderOpen],
  ["Projects", "/app/projects", FolderKanban],
  ["Knowledge Base", "/app/knowledge", BookOpen],
  ["Team", "/app/team", Users],
  ["Training", "/app/training", GraduationCap],
  ["Permits to work", "/app/permits", KeyRound],
  ["Incidents", "/app/incidents", Siren],
  ["Inspections", "/app/inspections", ClipboardCheck],
  ["Billing & credits", "/app/billing", CreditCard],
  ["Company", "/app/settings", Building2],
];


const Sidebar = ({ onNav }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex h-full flex-col bg-navy text-slate-300">
      <div className="flex h-16 items-center px-5"><Logo light to="/app" /></div>
      <div className="px-3">
        <Link to="/app/generate" onClick={onNav} data-testid="sidebar-create-ai" className="mb-4 flex items-center justify-center gap-2 rounded-xl bg-health py-2.5 text-sm font-semibold text-navy transition-transform hover:-translate-y-0.5"><Sparkles className="h-4 w-4" />Create with AI</Link>
      </div>
      <nav className="flex-1 space-y-0.5 px-3">
        {LINKS.map(([l, to, Icon, end]) => (
          <NavLink key={to} to={to} end={end} onClick={onNav} data-testid={`sidebar-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "hover:bg-white/5 hover:text-white"}`}>
            <Icon className="h-4 w-4" />{l}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="grid h-8 w-8 place-items-center rounded-full bg-health/20 text-sm font-semibold text-health">{user?.name?.[0]}</div>
          <div className="min-w-0 flex-1"><p className="truncate text-sm text-white">{user?.name}</p><p className="truncate text-xs text-slate-500">{user?.org?.trading_name || user?.org?.registered_name}</p></div>
          <button data-testid="sidebar-notification-settings" aria-label="Email notification settings" onClick={() => { nav("/app/notification-settings"); onNav?.(); }} className="rounded-md p-1.5 hover:bg-white/10"><Bell className="h-4 w-4" /></button>
          <button data-testid="logout-button" aria-label="Log out" onClick={async () => { await logout(); nav("/login"); }} className="rounded-md p-1.5 hover:bg-white/10"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

const Notifications = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  const unread = items.filter((i) => !i.read).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-button" className="relative grid h-9 w-9 place-items-center rounded-lg border hover:bg-accent" aria-label="Notifications">
          <Bell className="h-4 w-4" />{unread > 0 && <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-alert px-1 text-[10px] font-bold text-white">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3"><p className="text-sm font-semibold">Notifications</p><div className="flex gap-3"><Link to="/app/notification-settings" className="text-xs text-muted-foreground hover:text-foreground" data-testid="notification-settings-link">Email settings</Link><button className="text-xs text-muted-foreground hover:text-foreground" data-testid="notifications-read-all" onClick={() => api.post("/notifications/read-all").then(load)}>Mark all read</button></div></div>
        <div className="max-h-80 overflow-auto">
          {items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">You're all caught up.</p>}
          {items.map((n) => (
            <Link key={n.id} to={n.link || "#"} className={`block border-b px-4 py-3 text-sm hover:bg-accent ${n.read ? "" : "bg-health/5"}`}>
              <p className="font-medium">{n.title}</p><p className="text-xs text-muted-foreground">{n.body}</p>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const VerifyBanner = ({ user }) => user.email_verified ? null : (
  <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-[#FFA000]/10 px-6 py-2 text-sm" data-testid="verify-banner">
    <span>Please verify your email address ({user.email}).</span>
    <button className="font-medium underline" onClick={() => api.post("/auth/resend-verification").then(() => toast.success("Verification link re-issued"))}>Resend link</button>
  </div>
);

export default function AppLayout() {
  const { user } = useAuth();
  const [theme, setTheme] = useTheme();
  const [mobile, setMobile] = useState(false);
  const [ask, setAsk] = useState(false);
  if (user === null) return <Loader label="Loading Saflr" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.org_id) return <Navigate to={user.is_super_admin ? "/admin" : "/onboarding"} replace />;
  const sub = user.org?.subscription;
  return (
    <div className="flex min-h-screen bg-background">
      <aside className="fixed inset-y-0 hidden w-64 lg:block"><Sidebar /></aside>
      <Sheet open={mobile} onOpenChange={setMobile}><SheetContent side="left" className="w-64 border-0 p-0"><Sidebar onNav={() => setMobile(false)} /></SheetContent></Sheet>
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur-xl sm:px-6">
          <button className="lg:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu" aria-label="Menu"><Menu className="h-5 w-5" /></button>
          {sub && <span className="hidden rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground sm:inline" data-testid="plan-pill">{sub.plan_name} · {sub.status.replace("_", " ")}</span>}
          <div className="ml-auto flex items-center gap-2">
            <Link to="/app/billing" className="hidden items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm sm:flex" data-testid="credits-pill"><Coins className="h-4 w-4 text-[#FFA000]" />Credits</Link>
            <button onClick={() => setAsk(true)} data-testid="ask-saflr-open" className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm hover:bg-accent"><MessageCircle className="h-4 w-4 text-health" /><span className="hidden sm:inline">Ask Saflr</span></button>
            <Notifications />
            <ThemeToggle theme={theme} setTheme={setTheme} />
          </div>
        </header>
        <VerifyBanner user={user} />
        <main className="flex-1 px-4 py-8 sm:px-8"><Outlet /></main>
      </div>
      <AskSaflr open={ask} onOpenChange={setAsk} />
    </div>
  );
}
